import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Project, ProjectListItem, ScenePage } from "@proof/core";
import { ProofCanvas } from "@proof/ui";
import { useWorkspaceTransport } from "./WorkspaceContext";

const values = {};
const imageQueue: Array<() => void> = [];
let activeImages = 0;
function queuedImage<T>(work: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (imageQueue.length >= 100) {
      reject(new Error("Thumbnail queue busy"));
      return;
    }
    const start = () => {
      activeImages++;
      void work()
        .then(resolve, reject)
        .finally(() => {
          activeImages--;
          imageQueue.shift()?.();
        });
    };
    if (activeImages < 3) start();
    else imageQueue.push(start);
  });
}

/** Fetch only small, authorized image bytes as each row approaches the viewport. */
function ThumbnailImage({
  project,
}: {
  project: Pick<ProjectListItem, "id" | "tenantId" | "name" | "revision">;
}) {
  const transport = useWorkspaceTransport();
  const root = useRef<HTMLSpanElement>(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let started = false,
      objectUrl = "";
    setUrl("");
    setError(false);
    const start = () => {
      if (started) return;
      started = true;
      void queuedImage(async () => {
        if (controller.signal.aborted) return;
        const response = await transport.request(
          `/projects/${encodeURIComponent(project.id)}/thumbnail?revision=${project.revision}&scope=${encodeURIComponent(project.tenantId)}`,
          { signal: controller.signal },
        );
        const blob = await response.blob();
        if (blob.type !== "image/png" || blob.size > 1024 * 1024)
          throw new Error("Invalid thumbnail");
        if (!controller.signal.aborted) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      }).catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    };
    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) {
                observer?.disconnect();
                start();
              }
            },
            { rootMargin: "180px" },
          );
    if (observer && root.current) observer.observe(root.current);
    else start();
    return () => {
      controller.abort();
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [transport, project.id, project.tenantId, project.revision]);
  return (
    <span ref={root} className="project-thumbnail-small">
      {url ? (
        <img src={url} alt={`${project.name} thumbnail`} decoding="async" />
      ) : (
        <span
          role="img"
          aria-label={
            error
              ? `Preview unavailable for ${project.name}`
              : `Loading preview of ${project.name}`
          }
        >
          {error ? "!" : "…"}
        </span>
      )}
    </span>
  );
}

/** Catalog thumbnails load automatically without fetching full documents into the browser. */
export function CatalogProjectThumbnail({
  project,
  load,
}: {
  project: ProjectListItem;
  load: () => Promise<Project>;
}) {
  const [full, setFull] = useState<Project>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (full) return <ProjectThumbnail project={full} />;
  return (
    <button
      type="button"
      className="project-thumbnail"
      aria-label={`Preview ${project.name}`}
      title={error || `Load preview of ${project.name}`}
      disabled={loading}
      onClick={() => {
        setLoading(true);
        void load()
          .then(setFull)
          .catch((error) => setError(String(error.message || error)))
          .finally(() => setLoading(false));
      }}
    >
      <ThumbnailImage project={project} />
    </button>
  );
}
const Artwork = memo(function Artwork({
  project,
  large = false,
}: {
  project: Project;
  large?: boolean;
}) {
  const transport = useWorkspaceTransport();
  const [dimensions, setDimensions] = useState({
    width: project.pages[0].width || 612,
    height: project.pages[0].height || 792,
  });
  const layout = useCallback(
    (page: ScenePage) =>
      setDimensions((previous) =>
        previous.width === page.width && previous.height === page.height
          ? previous
          : { width: page.width, height: page.height },
      ),
    [],
  );
  const zoom = Math.min(
    (large ? Math.min(328, innerWidth - 56) : 56) /
      Math.max(1, dimensions.width),
    (large ? Math.max(1, Math.min(240, innerHeight - 132)) : 44) /
      Math.max(1, dimensions.height),
  );
  return (
    <div className="project-thumbnail-art">
      <ProofCanvas
        {...transport.proofServices}
        project={project}
        values={values}
        zoom={zoom}
        onLayout={layout}
      />
    </div>
  );
});

/** First-page artwork, with a delayed, hoverable preview outside the scrolling grid. */
export function ProjectThumbnail({ project }: { project: Project }) {
  const transport = useWorkspaceTransport();
  const root = useRef<HTMLDivElement>(null);
  const launch = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closing = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    width: number;
  }>();
  const id = useId();
  const cancelLaunch = () => {
    setPending(false);
    clearTimeout(launch.current);
    launch.current = undefined;
  };
  const keepOpen = () => {
    clearTimeout(closing.current);
    closing.current = undefined;
  };
  const hide = useCallback(() => {
    setPending(false);
    clearTimeout(launch.current);
    launch.current = undefined;
    clearTimeout(closing.current);
    closing.current = undefined;
    setPosition(undefined);
  }, []);
  const begin = () => {
    keepOpen();
    if (launch.current !== undefined || position) return;
    setPending(true);
    launch.current = setTimeout(() => {
      setPending(false);
      launch.current = undefined;
      const anchor = root.current?.getBoundingClientRect();
      if (
        !anchor ||
        anchor.bottom < 0 ||
        anchor.top > innerHeight ||
        anchor.right < 0 ||
        anchor.left > innerWidth
      )
        return;
      const width = Math.min(360, innerWidth - 24),
        height = Math.min(348, innerHeight - 24);
      const left =
        anchor.right + width + 12 <= innerWidth
          ? anchor.right + 12
          : Math.max(12, anchor.left - width - 12);
      const top = Math.max(12, Math.min(anchor.top, innerHeight - height - 12));
      setPosition({ left, top, width });
    }, 500);
  };
  const leave = () => {
    cancelLaunch();
    keepOpen();
    closing.current = setTimeout(hide, 150);
  };
  useEffect(() => {
    const element = root.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(
    () => () => {
      clearTimeout(launch.current);
      clearTimeout(closing.current);
    },
    [],
  );
  useEffect(() => {
    if (!position && !pending) return;
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") hide();
    };
    window.addEventListener("keydown", key);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [position, pending, hide]);
  return (
    <>
      <div
        className="project-thumbnail"
        ref={root}
        onPointerEnter={begin}
        onPointerLeave={leave}
      >
        <div aria-hidden="true" className="project-thumbnail-small">
          {transport.demo ? (
            visible && <Artwork project={project} />
          ) : (
            <ThumbnailImage project={project} />
          )}
        </div>
        <button
          type="button"
          className="project-thumbnail-trigger"
          aria-label={`Preview ${project.name}`}
          aria-expanded={!!position}
          aria-describedby={position ? id : undefined}
          onFocus={begin}
          onBlur={hide}
          onClick={() => (position ? hide() : begin())}
        />
      </div>
      {position &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            aria-label={`${project.name} larger preview`}
            className="project-thumbnail-popover"
            style={position}
            onPointerEnter={keepOpen}
            onPointerLeave={hide}
          >
            <strong>{project.name}</strong>
            <div className="project-thumbnail-large">
              <Artwork project={project} large />
            </div>
            <small>
              First page
              {project.pages.length > 1 ? ` of ${project.pages.length}` : ""}
            </small>
          </div>,
          document.body,
        )}
    </>
  );
}
